import React from "react";

import { LeaderboardRecord } from "../../models/LeaderboardRecord";
import { Game } from "../../models/Game";
import { GameState } from "../../models/Enums";
import ScoreService, {
  LeaderboardTabs as Tabs,
} from "../../services/RemoteScoreService";
import { Button } from "../../atoms/Buttons/Base";
import { Box } from "../../atoms/Layout/Box/Box";
import { GridRow } from "../../atoms/Layout/GridRow/GridRow";
import { DesktopQuery, MobileQuery } from "../../atoms/Layout/Responsive";
import { Text } from "../../atoms/Text/Text";

import classNames from "classnames";
import styles from "./Leaderboard.css";

type LeaderboardProps = {
  game: Game;
  gameState: GameState;
};

type LeaderboardState = {
  activeTab: Tabs;
  isLoading: boolean;
  list: LeaderboardRecord[];
};

export class Leaderboard extends React.PureComponent<
  LeaderboardProps,
  LeaderboardState
> {
  state = {
    activeTab: Tabs.TODAY,
    isLoading: true,
    list: [],
  };

  _mounted = false;

  componentWillUnmount() {
    this._mounted = false;
  }

  componentDidUpdate(prevProps: LeaderboardProps) {
    const { game, gameState } = this.props;

    // If game saved score, update LB. RemoteScoreService already patched LB rows with user's score
    const gameSavedScore =
      gameState !== prevProps.gameState &&
      gameState === GameState.GAME_SCORE_SAVED;
    const gameChanged = game.slug !== prevProps.game.slug;

    if (gameSavedScore || gameChanged) {
      this.loadTab(this.state.activeTab, game.groupSlug);
    }
  }

  componentDidMount() {
    this._mounted = true;
    this.loadTab(Tabs.TODAY);
  }

  loadTab(tab: Tabs, slug: string = this.props.game.groupSlug): Promise<any> {
    this.setState({ isLoading: true });
    return ScoreService.getLeaderboard(slug, tab).then((list) => {
      if (this._mounted) {
        this.setState({ activeTab: tab, list, isLoading: false });
      }
    });
  }

  onTabSelect(tab: Tabs) {
    this.loadTab(tab);
  }

  render() {
    const { activeTab } = this.state;
    const { game } = this.props;
    const title = Game.getOnPageTitle(game);

    return (
      <Container data-element-description="leaderboard">
        <table className={styles.table}>
          <caption>
            <Header data-element-description="leaderboard title">
              <Text
                size={title.length < 30 ? "xxl" : "xl"}
                width="100%"
                align="center"
                bold={true}
                color="inverted"
                className={styles.titleText}
              >
                {title}
              </Text>

              <DesktopQuery>
                <Text size="md" bold={true} color="inverted">
                  TOP SCORES
                </Text>
              </DesktopQuery>

              <MobileQuery>
                <Text size="md" color="inverted">
                  DAILY TOP SCORES
                </Text>
              </MobileQuery>
            </Header>
          </caption>
          <thead>
            <tr>
              <th colSpan={3}>
                <TabsList role="tablist">
                  {this.renderTab(
                    "Today",
                    "leaderboard today tab",
                    "Today Leaderboard",
                    Tabs.TODAY,
                    () => this.onTabSelect(Tabs.TODAY)
                  )}

                  {this.renderTab(
                    "This Week",
                    "leaderboard week tab",
                    "This Week Leaderboard",
                    Tabs.THIS_WEEK,
                    () => this.onTabSelect(Tabs.THIS_WEEK)
                  )}

                  {this.renderTab(
                    "This Month",
                    "leaderboard month tab",
                    "This Month Leaderboard",
                    Tabs.THIS_MONTH,
                    () => this.onTabSelect(Tabs.THIS_MONTH)
                  )}
                </TabsList>
              </th>
            </tr>
          </thead>
          {this.renderLBTable(activeTab)}

          <tfoot>
            <tr>
              <td colSpan={3}>
                <MobileQuery>
                  <PlayLinkContainer>
                    <PlayLink
                      data-element-description="leaderboard play now"
                      href={Game.getURLAlias(game)}
                    >
                      <Text size="md" bold={true}>
                        Play now
                      </Text>
                    </PlayLink>
                  </PlayLinkContainer>
                </MobileQuery>
              </td>
            </tr>
          </tfoot>
        </table>
      </Container>
    );
  }

  renderTab(
    tabName: string,
    description: string,
    label: string,
    tabKey: Tabs,
    onClick: any
  ) {
    const active = this.state.activeTab === tabKey;

    return (
      <Tab
        role="tab"
        data-element-description={description}
        aria-controls={tabKey}
        id={`tab${tabKey}`}
        aria-selected={active}
        aria-label={label}
        active={active}
        onClick={onClick}
      >
        <Text bold={true} size="xs">
          {tabName}
        </Text>
      </Tab>
    );
  }

  renderLBTable(tabId: string) {
    if (this.state.isLoading) {
      return (
        <tbody>
          <tr>
            <td colSpan={3} align="center">
              <MessageText
                color="accentLight"
                align="center"
                as="div"
                role="status"
                ariaLive="polite"
              >
                Loading...
              </MessageText>
            </td>
          </tr>
        </tbody>
      );
    }

    if (!this.state.list.length) {
      return (
        <tbody>
          <tr>
            <td colSpan={3} align="center">
              <MessageText color="accentLight" role="status" ariaLive="polite" align="center" as="div">
                There are no scores yet
              </MessageText>
            </td>
          </tr>
        </tbody>
      );
    }

    return (
      <React.Fragment>
        <tbody role="tabpanel" id={tabId} aria-labelledby={`tab${tabId}`}>
          <tr>
            <th className="thRank">
              <ListHead>
                <Box align="center" justify="center">
                  <ColumnName bold={true} size="xs">
                    Rank
                  </ColumnName>
                </Box>
              </ListHead>
            </th>

            <th>
              <ListHead>
                <Box align="center" justify="flex-start">
                  <ColumnName bold={true} size="xs">
                    Name
                  </ColumnName>
                </Box>
              </ListHead>
            </th>

            <th className="thScore">
              <ListHead>
                <Box align="center" justify="flex-start">
                  <ColumnName bold={true} size="xs">
                    Score
                  </ColumnName>
                </Box>
              </ListHead>
            </th>
          </tr>

          {this.renderLBRows(this.state.list)}
        </tbody>
      </React.Fragment>
    );
  }

  renderLBRows(list) {
    return list.slice(0, 10).map((user, ind) => (
      <tr key={ind}>
        <td>
          <UsersList>
            <UserRow key={ind}>
              <Box align="center" justify="center">
                <UserRank bold={true} size="lg">
                  {ind + 1}
                </UserRank>
              </Box>
            </UserRow>
          </UsersList>
        </td>

        <td>
          <UsersList>
            <UserRow key={ind}>
              <Box align="center" justify="flex-start">
                <UserName bold={true} size="lg">
                  {user.name}
                </UserName>
              </Box>
            </UserRow>
          </UsersList>
        </td>

        <td>
          <UsersList>
            <UserRow key={ind}>
              <Box align="center" justify="flex-end">
                <UserScore bold={true} size="lg">
                  {ScoreService.formatScore(user.score)}
                </UserScore>
              </Box>
            </UserRow>
          </UsersList>
        </td>
      </tr>
    ));
  }
}

const Container = (props: any) => (
  <div className={styles.container} {...props} />
);
const Header = (props: any) => <div className={styles.header} {...props} />;
const UserRow = (props: any) => (
  <GridRow className={styles.userRow} {...props} />
);
const TabsList = (props: any) => <div className={styles.tabsList} {...props} />;
const Tab = ({ active, ...props }: any) => (
  <Button
    className={classNames(styles.tab, { [styles.active]: !!active })}
    {...props}
  />
);
const MessageText = (props: any) => (
  <Text className={styles.messageText} {...props} />
);
const UsersList = (props: any) => (
  <div className={styles.usersList} {...props} />
);
const ListHead = (props: any) => (
  <GridRow className={styles.listHead} {...props} />
);
const ColumnName = (props: any) => (
  <Text className={styles.columnName} {...props} />
);
const UserRank = (props: any) => (
  <Text className={styles.userRank} {...props} />
);
const UserName = (props: any) => (
  <Text className={styles.userName} {...props} />
);
const UserScore = (props: any) => (
  <Text className={styles.userScore} {...props} />
);
const PlayLinkContainer = (props: any) => (
  <div className={styles.playLinkContainer} {...props} />
);
const PlayLink = (props: any) => <a className={styles.playLink} {...props} />;
